<script lang="ts">
	import { tooltip } from '$lib/tooltip/tootlip.svelte';
	import type { Snippet } from 'svelte';
	import { getTabNavContext } from './tabs.context.svelte';

	interface Props {
		id: string;
		icon?: Snippet;
		onclick?: (value: string) => void;
		children: Snippet;
	}
	let { id, icon, children, onclick }: Props = $props();

	const ctx = getTabNavContext();

	function handleClick() {
		onclick?.(id);
		ctx.onchange?.(id);
	}

	let labelEl: HTMLSpanElement;
	let tooltipText = $derived(labelEl?.textContent ?? '');
</script>

{#snippet content()}
	{#if icon}
		<span class="icon">{@render icon()}</span>
	{/if}
	<span class="label" bind:this={labelEl}>{@render children()}</span>
{/snippet}

{#if ctx.mode == 'button'}
	<button
		class="tab"
		class:auto-hide-labels={ctx.autoHideLabels}
		class:active={ctx.activeTab === id}
		class:alternate-active={ctx.orientation === 'vertical'}
		onclick={() => handleClick()}
		use:tooltip={tooltipText}
	>
		{@render content()}
	</button>
{:else}
	<a
		class="tab"
		class:auto-hide-labels={ctx.autoHideLabels}
		class:active={ctx.activeTab === id}
		class:alternate-active={ctx.orientation === 'vertical'}
		href="{ctx.baseURL}{id}"
		onclick={() => onclick?.(id)}
		use:tooltip={tooltipText}
	>
		{@render content()}
	</a>
{/if}

<style lang="scss">
	@use '@astlai/common/styles/mixins' as lib;

	.tab {
		display: flex;
		flex-direction: row;
		align-items: center;
		gap: 0.5rem;
		padding: 0.5rem 0.75rem;
		font-size: 0.875rem;
		color: var(--text-secondary);
		background: none;
		border: none;
		border-radius: 0.5rem;
		cursor: pointer;
		flex: 0 0 auto;
		height: auto;
		overflow: hidden;
		min-width: 0;
		height: auto;

		.icon {
			flex-shrink: 0;
			display: flex;
		}

		.label {
			min-width: 0;
			@include lib.ellipsis();
		}

		@include lib.bp(gt-xs) {
			flex: 0 1 auto;
		}

		&:hover {
			color: var(--text-primary);
			background: var(--state-hover);
		}

		&.active {
			color: var(--text-primary);
			background: var(--surface-accent);
		}

		&.alternate-active {
			color: var(--text-primary);
			&.active {
				@include lib.glass-surface;
			}
		}

		&.auto-hide-labels {
			@container (max-width: 380px) {
				flex: 1 1 0;
				.label {
					display: none;
				}
			}
		}
	}
</style>
